import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Stack,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useMediaQuery,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import useAxiosPrivate from "../../../Hooks/useAxiosPrivate";
import styles from './Roles.module.css';

const Roles = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [sortModel, setSortModel] = useState([]);

  // Delete confirm dialog state
  const [deleteDialog, setDeleteDialog] = useState({ open: false, role: null });
  const [isDeletingRole, setIsDeletingRole] = useState(false);

  const formatApiError = (e) => {
    const status = e?.response?.status;
    const data = e?.response?.data;
    if (data?.error) return data.error;
    if (typeof data === 'string') return `${status ? `${status}: ` : ''}${data}`.trim();
    if (data) return `${status ? `${status}: ` : ''}${JSON.stringify(data)}`.trim();
    return e?.message || 'Unknown error';
  };

  const isNotFound = (e) => e?.response?.status === 404;

  const fetchData = useCallback(async ({ silent = false } = {}) => {
      setIsLoading(true);
    setError(false);
    try {
      // Prefer /api/roles if available, otherwise fall back to basic model endpoint.
      let resp;
      try {
        resp = await axiosPrivate.get('/api/roles');
      } catch (e) {
        if (!isNotFound(e)) throw e;
        resp = await axiosPrivate.get('/api/basic/cms_role');
      }

      const rows = Array.isArray(resp?.data) ? resp.data : [];
      setAllData(rows);
      setFilteredData(rows);
      if (!silent) enqueueSnackbar('Roles loaded', { variant: 'success' });
    } catch (e) {
      setError(true);
      if (!silent) enqueueSnackbar(formatApiError(e) || 'Failed to load roles', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [axiosPrivate, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    const next = (allData || []).filter((r) => {
      const title = String(r?.RoleDisplayName ?? '').toLowerCase();
      const name = String(r?.RoleName ?? '').toLowerCase();
      const desc = String(r?.RoleDescription ?? '').toLowerCase();
      return title.includes(lower) || name.includes(lower) || desc.includes(lower);
    });
    setFilteredData(next);
    setPaginationModel((prev) => ({ ...prev, page: 0 })); // reset to first page on search
  }, [searchTerm, allData]);

  const openDeleteDialog = (role) => setDeleteDialog({ open: true, role });
  const closeDeleteDialog = () => {
    if (isDeletingRole) return;
    setDeleteDialog({ open: false, role: null });
  };

  const handleConfirmDelete = async () => {
    const roleId = deleteDialog?.role?.RoleID;
    if (!roleId) return;
    setIsDeletingRole(true);
    try {
      try {
        await axiosPrivate.delete(`/api/roles/${roleId}`);
      } catch (e) {
        if (!isNotFound(e)) throw e;
        await axiosPrivate.delete(`/api/basic/cms_role/${roleId}`);
      }
      enqueueSnackbar('Role deleted', { variant: 'success' });
      setDeleteDialog({ open: false, role: null });
      await fetchData({ silent: true });
    } catch (e) {
      enqueueSnackbar(formatApiError(e) || 'Failed to delete role', { variant: 'error' });
    } finally {
      setIsDeletingRole(false);
    }
  };

  const columns = useMemo(() => [
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      align: 'center',
      headerAlign: 'center',
      getActions: (params) => [
        <Tooltip key="edit" title="Edit Role" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<EditIcon />}
              label="Edit Role"
              showInMenu={false}
              onClick={() => navigate(`/admin/roles/edit/${params?.row?.RoleID}`)}
            />
          </span>
        </Tooltip>,
        <Tooltip key="delete" title="Delete Role" arrow placement="top">
          <span>
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete Role"
              showInMenu={false}
              onClick={() => openDeleteDialog(params.row)}
            />
          </span>
        </Tooltip>,
      ]
    },
    {
      field: 'RoleDisplayName',
      headerName: 'Title',
      minWidth: 300,
      flex: 1
    }
  ], [navigate]);

  if (isLoading) {
    return (
      <main>
        {/* Blue Header Banner */}
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Manage Roles</h1>
                <p>Permission groups that determine user access</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.loadingContainer}>
            <CircularProgress />
            <Typography className={styles.loadingText}>Loading roles...</Typography>
          </Box>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main>
        {/* Blue Header Banner */}
        <div className="full-row light-blue">
          <div className="container-fluid">
            <div className="row text-white pt-3 pb-3">
              <div className="col-md-12 text-white">
                <h1 className="text-uppercase">Manage Roles</h1>
                <p>Permission groups that determine user access</p>
              </div>
            </div>
          </div>
        </div>
        <div className="full-row white">
          <Box className={styles.errorContainer}>
            <Typography color="error">Error occurred while fetching roles, Please contact an admin</Typography>
          </Box>
        </div>
      </main>
    );
  }

  // Custom pagination (same UX as attendees)
  const totalCount = filteredData.length;
  const pageCount = Math.ceil(totalCount / paginationModel.pageSize) || 1;
  const startIndex = paginationModel.page * paginationModel.pageSize;
  const endIndex = startIndex + paginationModel.pageSize;
  // Sort the ENTIRE filtered list (not just the current page) before paginating.
  const sortedData = (() => {
    if (!sortModel.length) return filteredData;
    const { field, sort } = sortModel[0];
    if (!field || !sort) return filteredData;
    const dir = sort === 'desc' ? -1 : 1;
    return [...filteredData].sort((a, b) => {
      const av = String(a?.[field] ?? '').toLowerCase();
      const bv = String(b?.[field] ?? '').toLowerCase();
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  })();
  const paginatedData = sortedData.slice(startIndex, endIndex);

  return (
    <main>
      {/* Blue Header Banner */}
      <div className="full-row light-blue">
        <div className="container-fluid">
          <div className="row text-white pt-3 pb-3">
            <div className="col-md-12 text-white">
              <h1 className="text-uppercase">Manage Roles</h1>
              <p>Permission groups that determine user access</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="full-row white">
      <Box className={styles.container}>
      <Paper className={styles.searchPaper}>
        <Box className={styles.searchContainer}>
          <Box className={styles.buttonContainer}>
            <Button
              variant="contained"
              onClick={() => navigate('/admin/roles/new')}
              startIcon={<AddIcon />}
              className={styles.addButton}
            >
              New Role
            </Button>
          </Box>

          <TextField
            className={styles.searchField}
            label="Search roles"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
        </Box>
      </Paper>

      <Paper className={styles.dataGridPaper}>
        <DataGrid
          className="rolesDataGrid"
          rows={paginatedData}
          columns={columns}
          getRowId={(row) => row.RoleID}
          disableRowSelectionOnClick
          autoHeight
          hideFooterPagination
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(m) => {
            setSortModel(m);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
          components={{
            NoRowsOverlay: () => (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', py: 4, gap: 0.5 }}>
                <Typography sx={{ fontWeight: 600, color: '#21252C' }}>No roles found</Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>Try a different search term.</Typography>
              </Box>
            ),
          }}
        />
      </Paper>

      {/* Custom Pagination */}
      <Paper className={styles.paginationPaper}>
        <Stack
          className={styles.paginationStack}
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          sx={{ width: '100%' }}
        >
          <Typography variant="body2" color="text.secondary">
            Showing {totalCount > 0 ? (paginationModel.page * paginationModel.pageSize) + 1 : 0} to{' '}
            {Math.min((paginationModel.page + 1) * paginationModel.pageSize, totalCount)} of {totalCount} roles
          </Typography>

          <Stack className={styles.paginationControls} direction="row" alignItems="center">
            <Pagination
              count={pageCount}
              page={paginationModel.page + 1}
              onChange={(_, page) => setPaginationModel((prev) => ({ ...prev, page: page - 1 }))}
              color="primary"
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
              className="rolesPagination"
            />
          </Stack>

          <Box className={styles.pageSizeSelector}>
            <Typography variant="body2" color="text.secondary">
              Show:
            </Typography>
            <TextField
              select
              size="small"
              value={paginationModel.pageSize}
              onChange={(e) => {
                const newLimit = parseInt(e.target.value);
                setPaginationModel((prev) => ({ ...prev, pageSize: newLimit, page: 0 }));
              }}
              SelectProps={{ native: true }}
              className={`${styles.pageSizeField} pageSizeField`}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              per page
            </Typography>
          </Box>
        </Stack>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={closeDeleteDialog}>
        <DialogTitle className={styles.dialogTitle}>Delete Role</DialogTitle>
        <DialogContent>
          <Typography className={styles.dialogText}>
            Are you sure you want to delete{' '}
            <strong>{deleteDialog?.role?.RoleDisplayName ?? 'this role'}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button onClick={closeDeleteDialog} disabled={isDeletingRole} className={styles.cancelButton}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeletingRole}
            variant="contained"
            color="error"
            className={styles.confirmButtonDelete}
          >
            {isDeletingRole ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
      </div>
    </main>
  );
}

export default Roles


